import React from 'react';

import i18n from '@nextroll/ar-i18n';
import { Placements, Popper, Tooltip } from '@nextroll/ar-style-base';

import { REPORT_URL } from 'util/urls';
import { isRoleUserExternalOrEngineer } from 'util/util';
import { WorkflowBaseType, WORKFLOW_BASE } from 'types/rofl';


export const TotalEnrolledTooltip = (props: {
    children: React.ReactNode;
}) => {
    const tooltipContent = (
        <Tooltip
            className='workflow-table-total-enrolled-tooltip'
            placement={Placements.TOP}
        >
            {i18n.gettext('The total amount that entered the workflow since the initial launch.')}
        </Tooltip>
    );

    return (
        <Popper
            hoverContent={tooltipContent}
            breakOutOfOverflow
        >
            {props.children}
        </Popper>
    );
};

export const ViewLogTooltip = (props: {
    children: React.ReactNode;
    totalEnrolled: number;
    createdAt: string;
    workflowEid: string;
    advertisableEid: string;
    actualUserRole: string | undefined;
    base?: WorkflowBaseType;
}) => {
    const downloadUrl = `${REPORT_URL}/${props.workflowEid}/contacts/?advertisable_eid=${props.advertisableEid}`;
    const tooltipContent = (
        <Tooltip
            className='workflow-table-view-log-tooltip'
            placement={Placements.TOP}
        >
            <>
                <span className='workflow-table-view-log-tooltip-message'>
                    {props.base === WORKFLOW_BASE.ACCOUNTS ? i18n.ngettext(
                        '<Span>{{ totalEnrolled }} account</Span> entered since {{ createdAt }}',
                        '<Span>{{ totalEnrolled }} accounts</Span> entered since {{ createdAt }}',
                        props.totalEnrolled,
                        {
                            Span: <span className='num-contacts' />,
                            totalEnrolled: i18n.numeral(props.totalEnrolled).format('0,0'),
                            createdAt: i18n.moment(props.createdAt).format('MMM D, YYYY'),
                        }
                    ) : i18n.ngettext(
                        '<Span>{{ totalEnrolled }} contact</Span> entered since {{ createdAt }}',
                        '<Span>{{ totalEnrolled }} contacts</Span> entered since {{ createdAt }}',
                        props.totalEnrolled,
                        {
                            Span: <span className='num-contacts' />,
                            totalEnrolled: i18n.numeral(props.totalEnrolled).format('0,0'),
                            createdAt: i18n.moment(props.createdAt).format('MMM D, YYYY'),
                        }
                    )}
                </span>
                {isRoleUserExternalOrEngineer(props.actualUserRole) && (
                    <a role='button' href={downloadUrl} download>
                        <i className='fa fa-file-download mr-1' />
                        {i18n.gettext('Download 30-day Report')}
                    </a>
                )}
            </>
        </Tooltip>
    );

    return (
        <Popper
            hoverContent={tooltipContent}
        >
            {props.children}
        </Popper>
    );
};

/**
 * This tooltip is displayed on the "Processing" label in the workflows table.
 */
export const AwaitingFirstRunTooltip = (props: { children: React.ReactNode }) => {
    const tooltipContent = (
        <Tooltip
            className='workflow-table-awaiting-first-run-tooltip'
            placement={Placements.TOP}
        >
            <span>{i18n.gettext('This Workflow was launched recently. It will take up to 20 minutes for the first run to be completed.')}</span>
        </Tooltip>
    );
    return (
        <Popper
            hoverContent={tooltipContent}
        >
            {props.children}
        </Popper>
    );
};

/**
 * Element for the tooltip when the user tries to turn on a workflow but the limit has been reached.
 *
 * @param maxOnWorkflowsLimit The maximum number of workflows that can be in the 'on' state for the advertisable.
 * @param canTurnOnWorkflow Whether the user can turn on a workflow. This is true when the number of workflows in the
 * 'on' state is less than the limit.
 * @param placement The placement of the tooltip.
 */
export const MaxOnWorkflowsLimitReachedTooltip = ({ maxOnWorkflowsLimit, canTurnOnWorkflow, placement, children }: {
    maxOnWorkflowsLimit?: number;
    canTurnOnWorkflow?: boolean;
    placement?: string;
    children: JSX.Element;
}) => {
    if (canTurnOnWorkflow) {
        return children;
    }

    const tooltipContent = (
        <Tooltip
            className='workflows-workflow-list-table-action-option-tooltip'
            placement={placement || Placements.BOTTOM}
        >
            {i18n.gettext(
                'You reached your {{ maxOnWorkflowsLimit }} running workflows limit. Pause a running workflow '
                + 'before creating or resuming a workflow, or contact your Account Manager to increase your limit.',
                { maxOnWorkflowsLimit },
            )}
        </Tooltip>
    );

    return <Popper hoverContent={tooltipContent}>{children}</Popper>;
};
